import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bookmark,
  Building2,
  Camera as CamIcon,
  ChevronDown,
  ChevronUp,
  Clapperboard,
  Clock,
  Eye,
  Expand,
  MapPin,
  Pencil,
  Plus,
  Repeat,
  ScanText,
  Search,
  Server,
  Store as StoreIcon,
  Trash2,
} from "lucide-react";

import {
  api,
  ApiError,
  type SimOffset,
  type SimVideoFolder,
  type MonitoringCamera,
  type Store,
  type StoreInput,
  type Stores,
} from "../../api";
import { FieldLabel, InfoHint } from "../../components/InfoHint";
import { Modal } from "../../components/Modal";
import { Select } from "../../components/Select";
import { usePipelineControl } from "../../components/PipelineControlContext";
import { SearchableSelect } from "../../components/SearchableSelect";
import { useScope } from "../../components/ScopeContext";
import { searchItems } from "../../fuzzy";
import { EmptyState, Note, SettingsSection } from "./shared";

/**
 * Stores: create, edit, delete, and see at a glance which are simulations.
 *
 * There is no "activate" action here any more — every store runs at once, and
 * which one you are looking at is the scope picker in the header.
 */
export function StoresSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { runControl } = usePipelineControl();
  const { scope, selectStore } = useScope();
  const stores = useQuery({ queryKey: ["stores"], queryFn: api.stores.get });
  const [draft, setDraft] = useState<(StoreInput & { existing?: string }) | null>(
    null,
  );
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Store | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["stores"] });
    qc.invalidateQueries({ queryKey: ["cameras"] });
    qc.invalidateQueries({ queryKey: ["monitoring"] });
  };

  const create = useMutation({
    mutationFn: (body: StoreInput) =>
      body.is_simulation
        ? runControl("Starting the simulation…", () => api.stores.create(body), {
            waitReady: true,
          })
        : api.stores.create(body),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      invalidate();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not create the store."),
  });

  const update = useMutation({
    mutationFn: ({
      name,
      body,
      waitReady,
    }: {
      name: string;
      body: StoreInput;
      waitReady?: boolean;
    }) => {
      const patch = {
        name: body.name,
        display_name: body.display_name,
        location: body.location,
        is_simulation: body.is_simulation,
        sim_videos_subdir: body.sim_videos_subdir,
        sim_start_time: body.sim_start_time,
        sim_loop: body.sim_loop,
        sim_live: body.sim_live,
        sim_offsets: body.sim_offsets,
        sim_copies: body.sim_copies,
        min_crop_area_px2: body.min_crop_area_px2,
        crop_expand_pct: body.crop_expand_pct,
        ai_ingest: body.ai_ingest,
        customer_id: body.customer_id ?? null,
      };
      if (waitReady) {
        return runControl("Updating the simulation…", () => api.stores.update(name, patch), {
          waitReady: true,
        });
      }
      return api.stores.update(name, patch);
    },
    onSuccess: (updated, vars) => {
      if (
        scope.kind === "store" &&
        scope.store === vars.name &&
        updated.name &&
        updated.name !== vars.name
      ) {
        selectStore(updated.name);
      }
      setDraft(null);
      setError(null);
      invalidate();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not save the store."),
  });

  const jump = useMutation({
    mutationFn: ({ name, offset }: { name: string; offset: string }) =>
      runControl("Seeking the simulation…", () => api.stores.update(name, { sim_start_time: offset }), {
        waitReady: true,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not jump to that position."),
  });

  const replay = useMutation({
    mutationFn: (name: string) =>
      runControl("Starting the simulation…", () => api.stores.replay(name), {
        waitReady: true,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not start the simulation."),
  });

  const setLive = useMutation({
    mutationFn: ({ name, live }: { name: string; live: boolean }) =>
      api.stores.update(name, { sim_live: live }),
    onMutate: async ({ name, live }) => {
      await qc.cancelQueries({ queryKey: ["stores"] });
      await qc.cancelQueries({ queryKey: ["monitoring", "cameras"] });
      const prevStores = qc.getQueryData<Stores>(["stores"]);
      if (prevStores) {
        qc.setQueryData<Stores>(["stores"], {
          ...prevStores,
          stores: prevStores.stores.map((s) =>
            s.name === name ? { ...s, sim_live: live } : s,
          ),
        });
      }
      const prevCams = qc.getQueriesData<MonitoringCamera[]>({
        queryKey: ["monitoring", "cameras"],
      });
      for (const [key, cams] of prevCams) {
        if (!cams) continue;
        qc.setQueryData(
          key,
          cams.map((c) =>
            c.store === name
              ? {
                  ...c,
                  sim_live: live,
                  recovering: live ? c.recovering : false,
                  healthy: live ? c.healthy : false,
                  health_reason: live ? c.health_reason : "Live video is off",
                }
              : c,
          ),
        );
      }
      return { prevStores, prevCams };
    },
    onError: (e: unknown, _vars, ctx) => {
      if (ctx?.prevStores) qc.setQueryData(["stores"], ctx.prevStores);
      if (ctx?.prevCams) {
        for (const [key, cams] of ctx.prevCams) {
          qc.setQueryData(key, cams);
        }
      }
      setError(e instanceof ApiError ? e.message : "Could not update live video.");
    },
    onSuccess: () => setError(null),
    onSettled: () => invalidate(),
  });

  const setIngest = useMutation({
    mutationFn: ({ name, ingest }: { name: string; ingest: boolean }) =>
      api.stores.update(name, { ai_ingest: ingest }),
    onMutate: async ({ name, ingest }) => {
      await qc.cancelQueries({ queryKey: ["stores"] });
      const prevStores = qc.getQueryData<Stores>(["stores"]);
      if (prevStores) {
        qc.setQueryData<Stores>(["stores"], {
          ...prevStores,
          stores: prevStores.stores.map((s) =>
            s.name === name ? { ...s, ai_ingest: ingest } : s,
          ),
        });
      }
      return { prevStores };
    },
    onError: (e: unknown, _vars, ctx) => {
      if (ctx?.prevStores) qc.setQueryData(["stores"], ctx.prevStores);
      setError(e instanceof ApiError ? e.message : "Could not update AI ingest.");
    },
    onSuccess: () => setError(null),
    onSettled: () => invalidate(),
  });

  const setHls = useMutation({
    mutationFn: ({ name, hls }: { name: string; hls: boolean }) =>
      api.stores.update(name, { live_hls_fallback: hls }),
    onMutate: async ({ name, hls }) => {
      await qc.cancelQueries({ queryKey: ["stores"] });
      const prevStores = qc.getQueryData<Stores>(["stores"]);
      if (prevStores) {
        qc.setQueryData<Stores>(["stores"], {
          ...prevStores,
          stores: prevStores.stores.map((s) =>
            s.name === name ? { ...s, live_hls_fallback: hls } : s,
          ),
        });
      }
      return { prevStores };
    },
    onError: (e: unknown, _vars, ctx) => {
      if (ctx?.prevStores) qc.setQueryData(["stores"], ctx.prevStores);
      setError(e instanceof ApiError ? e.message : "Could not update Live view over HLS.");
    },
    onSuccess: () => setError(null),
    onSettled: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ["monitoring", "cameras"] });
    },
  });

  const remove = useMutation({
    mutationFn: (name: string) => api.stores.delete(name),
    onSuccess: () => {
      setConfirmDelete(null);
      setDraft(null);
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => {
      setConfirmDelete(null);
      setError(e instanceof ApiError ? e.message : "Could not delete the store.");
    },
  });

  const all = stores.data?.stores ?? [];
  const simFolders = stores.data?.sim_videos ?? [];
  const libraryMounted = stores.data?.sim_library_mounted ?? true;

  const visible = useMemo(
    () => searchItems(all, query, (s) => [s.display_name, s.name, s.location]),
    [all, query],
  );
  const live = useMemo(
    () => visible.filter((s) => !s.is_simulation),
    [visible],
  );
  const simulated = useMemo(
    () => visible.filter((s) => s.is_simulation),
    [visible],
  );

  return (
    <SettingsSection
      title="Stores"
      description="Each store is a site, with its own cameras and recorders."
      hint={
        <>
          Every store records at the same time. Choosing one in the{" "}
          <b>Select store</b> picker only changes what you see — it does not
          stop the others, and it does not affect other people signed in.
        </>
      }
      actions={
        canEdit && (
          <button
            className="bf-button"
            onClick={() =>
              setDraft({
                name: "",
                display_name: "",
                location: "",
                is_simulation: false,
                sim_videos_subdir: "",
                sim_start_time: "",
                sim_loop: true,
                sim_live: true,
                sim_offsets: [],
                sim_copies: 1,
                min_crop_area_px2: 12000,
                crop_expand_pct: 20,
                ai_ingest: true,
              })
            }
          >
            <Plus size={16} className="mr-2" /> Add store
          </button>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}

      {all.length > 4 && (
        <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2">
          <Search size={15} className="shrink-0 text-bf-muted" />
          <input
            className="w-full bg-transparent text-sm text-bf-text outline-none placeholder:text-bf-muted"
            placeholder="Search stores by name or location…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search stores"
          />
        </label>
      )}

      {stores.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading stores…</div>
      ) : all.length === 0 ? (
        <EmptyState
          icon={StoreIcon}
          title="No stores yet"
          hint="A store groups the cameras at one site. Create one before adding cameras or recorders."
          action={
            canEdit && (
              <button
                className="bf-button"
                onClick={() =>
                  setDraft({
                    name: "",
                    display_name: "",
                    location: "",
                    is_simulation: false,
                    sim_videos_subdir: "",
                    sim_start_time: "",
                    sim_loop: true,
                    sim_live: true,
                    sim_offsets: [],
                    sim_copies: 1,
                    min_crop_area_px2: 12000,
                    crop_expand_pct: 20,
                    ai_ingest: true,
                  })
                }
              >
                <Plus size={16} className="mr-2" /> Add your first store
              </button>
            )
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState title="No store matches that search." />
      ) : (
        <div className="space-y-8">
          {live.length > 0 && (
            <section className="space-y-3">
              <StoreSectionHeading label="Live stores" accent="from-bf-violet" />
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {live.map((s) => (
                  <StoreCard
                    key={s.name}
                    store={s}
                    canEdit={canEdit}
                    scoped={scope.kind === "store" && scope.store === s.name}
                    jumpPending={jump.isPending && jump.variables?.name === s.name}
                    replayPending={
                      replay.isPending && replay.variables === s.name
                    }
                    livePending={
                      setLive.isPending && setLive.variables?.name === s.name
                    }
                    ingestPending={
                      setIngest.isPending && setIngest.variables?.name === s.name
                    }
                    onView={() => selectStore(s.name)}
                    onJump={(offset) => jump.mutate({ name: s.name, offset })}
                    onReplay={() => replay.mutate(s.name)}
                    onLive={(live) => setLive.mutate({ name: s.name, live })}
                    onIngest={(ingest) => setIngest.mutate({ name: s.name, ingest })}
                    hlsPending={setHls.isPending && setHls.variables?.name === s.name}
                    onHls={(hls) => setHls.mutate({ name: s.name, hls })}
                    onEdit={() =>
                      setDraft({
                        existing: s.name,
                        name: s.name,
                        display_name: s.display_name,
                        location: s.location,
                        is_simulation: s.is_simulation,
                        sim_videos_subdir: s.sim_videos_subdir,
                        sim_start_time: s.sim_start_time,
                        sim_loop: s.sim_loop,
                        sim_live: s.sim_live,
                        sim_offsets: s.sim_offsets,
                        sim_copies: s.sim_copies,
                        min_crop_area_px2: s.min_crop_area_px2,
                        crop_expand_pct: s.crop_expand_pct,
                        ai_ingest: s.ai_ingest,
                        customer_id: s.customer_id,
                      })
                    }
                  />
                ))}
              </div>
            </section>
          )}
          {simulated.length > 0 && (
            <section className="space-y-3">
              <StoreSectionHeading
                label="Simulation stores"
                accent="from-bf-magenta"
              />
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {simulated.map((s) => (
                  <StoreCard
                    key={s.name}
                    store={s}
                    canEdit={canEdit}
                    scoped={scope.kind === "store" && scope.store === s.name}
                    jumpPending={jump.isPending && jump.variables?.name === s.name}
                    replayPending={
                      replay.isPending && replay.variables === s.name
                    }
                    livePending={
                      setLive.isPending && setLive.variables?.name === s.name
                    }
                    ingestPending={
                      setIngest.isPending && setIngest.variables?.name === s.name
                    }
                    onView={() => selectStore(s.name)}
                    onJump={(offset) => jump.mutate({ name: s.name, offset })}
                    onReplay={() => replay.mutate(s.name)}
                    onLive={(live) => setLive.mutate({ name: s.name, live })}
                    onIngest={(ingest) => setIngest.mutate({ name: s.name, ingest })}
                    hlsPending={setHls.isPending && setHls.variables?.name === s.name}
                    onHls={(hls) => setHls.mutate({ name: s.name, hls })}
                    onEdit={() =>
                      setDraft({
                        existing: s.name,
                        name: s.name,
                        display_name: s.display_name,
                        location: s.location,
                        is_simulation: s.is_simulation,
                        sim_videos_subdir: s.sim_videos_subdir,
                        sim_start_time: s.sim_start_time,
                        sim_loop: s.sim_loop,
                        sim_live: s.sim_live,
                        sim_offsets: s.sim_offsets,
                        sim_copies: s.sim_copies,
                        min_crop_area_px2: s.min_crop_area_px2,
                        crop_expand_pct: s.crop_expand_pct,
                        ai_ingest: s.ai_ingest,
                        customer_id: s.customer_id,
                      })
                    }
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {draft && !confirmDelete && (
        <StoreDraftModal
          draft={draft}
          simFolders={simFolders}
          libraryMounted={libraryMounted}
          takenNames={all.map((s) => s.name)}
          pending={create.isPending || update.isPending}
          onDelete={
            draft.existing
              ? () => {
                  const store = all.find((row) => row.name === draft.existing);
                  if (store) setConfirmDelete(store);
                }
              : undefined
          }
          onClose={() => {
            setDraft(null);
            setError(null);
          }}
          onSubmit={(body) => {
            const playbackChanged =
              Boolean(body.is_simulation) &&
              (!draft.existing ||
                (body.sim_start_time ?? "") !== (draft.sim_start_time ?? "") ||
                (body.sim_videos_subdir || "") !== (draft.sim_videos_subdir || "") ||
                (body.sim_copies ?? 1) !== (draft.sim_copies ?? 1) ||
                Boolean(body.is_simulation) !== Boolean(draft.is_simulation));
            if (draft.existing) {
              update.mutate({ name: draft.existing, body, waitReady: playbackChanged });
            } else create.mutate(body);
          }}
        />
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">
              Delete “{confirmDelete.display_name || confirmDelete.name}”?
            </h3>
            <p className="text-sm text-bf-muted">
              {confirmDelete.camera_count > 0 || confirmDelete.nvr_count > 0 ? (
                <>
                  This store still has {confirmDelete.camera_count} camera
                  {confirmDelete.camera_count === 1 ? "" : "s"}
                  {confirmDelete.nvr_count > 0 &&
                    ` and ${confirmDelete.nvr_count} recorder${confirmDelete.nvr_count === 1 ? "" : "s"}`}
                  . Move or delete them first — deleting the store will not
                  remove them.
                </>
              ) : (
                "The store has no cameras or recorders, so nothing else is affected. Captured data already recorded under this name is kept."
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="bf-button bg-white/10 bg-none"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={remove.isPending}
                onClick={() => remove.mutate(confirmDelete.name)}
              >
                {remove.isPending ? "Deleting…" : "Delete store"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsSection>
  );
}

function StoreSectionHeading({
  label,
  accent,
}: {
  label: string;
  accent: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <h3 className="shrink-0 text-xs font-medium tracking-[0.06em] text-white/70">
        {label}
      </h3>
      <span
        className={
          "h-px min-w-6 flex-1 bg-gradient-to-r to-transparent " + accent
        }
      />
    </div>
  );
}

function StoreSwitch({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 pt-1">
      <span className="flex items-center gap-1.5">
        <span className="text-sm text-bf-muted">{label}</span>
        <InfoHint>{hint}</InfoHint>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={
          "relative h-5 w-9 shrink-0 rounded-full transition disabled:opacity-40 " +
          (checked ? "bg-bf-violet" : "bg-white/15")
        }
      >
        <span
          className={
            "absolute top-0.5 h-4 w-4 rounded-full bg-white transition " +
            (checked ? "left-4" : "left-0.5")
          }
        />
      </button>
    </div>
  );
}

function StoreCard({
  store: s,
  canEdit,
  scoped,
  jumpPending,
  replayPending,
  livePending,
  ingestPending,
  onView,
  onJump,
  onReplay,
  onLive,
  onIngest,
  hlsPending,
  onHls,
  onEdit,
}: {
  store: Store;
  canEdit: boolean;
  scoped: boolean;
  jumpPending: boolean;
  replayPending: boolean;
  livePending: boolean;
  ingestPending: boolean;
  onView: () => void;
  onJump: (offset: string) => void;
  onReplay: () => void;
  onLive: (live: boolean) => void;
  onIngest: (ingest: boolean) => void;
  hlsPending: boolean;
  onHls: (hls: boolean) => void;
  onEdit: () => void;
}) {
  return (
    <article
      className={
        "bf-card flex h-full flex-col gap-3 " +
        (scoped ? "ring-1 ring-inset ring-bf-violet/50" : "")
      }
    >
      <div className="min-w-0">
        <h3 className="truncate text-base font-semibold text-white">
          {s.display_name || s.name}
        </h3>
        {s.display_name && s.display_name !== s.name && (
          <p className="truncate text-xs text-bf-muted">{s.name}</p>
        )}
      </div>

      <dl className="flex-1 space-y-1 text-sm text-bf-muted">
        {s.location && (
          <div className="flex items-center gap-2">
            <MapPin size={13} className="shrink-0" />
            <span className="truncate">{s.location}</span>
          </div>
        )}
        {s.customer_name && (
          <div className="flex items-center gap-2">
            <Building2 size={13} className="shrink-0" />
            <span className="truncate">{s.customer_name}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <CamIcon size={13} className="shrink-0" />
          <span>
            {s.camera_count} camera{s.camera_count === 1 ? "" : "s"}
            {s.camera_count > 0 && ` (${s.ai_camera_count} AI)`}
          </span>
        </div>
        {s.nvr_count > 0 && (
          <div className="flex items-center gap-2">
            <Server size={13} className="shrink-0" />
            <span>
              {s.nvr_count} recorder{s.nvr_count === 1 ? "" : "s"}
            </span>
          </div>
        )}
        {s.is_simulation && (
          <div className="flex items-center gap-2">
            <Clapperboard size={13} className="shrink-0" />
            <span className="truncate font-mono text-xs">
              {s.sim_videos_subdir || "no folder set"}
            </span>
            {(s.sim_copies ?? 1) > 1 && (
              <span className="shrink-0 text-xs">{s.sim_copies} streams per clip</span>
            )}
          </div>
        )}
        {s.is_simulation && (
          <div className="flex items-center gap-2">
            <Repeat size={13} className="shrink-0" />
            <span>
              {s.sim_loop !== false
                ? "Loops when the video ends"
                : s.sim_finished
                  ? "Finished"
                  : "Plays once"}
            </span>
          </div>
        )}
        <StoreSwitch
          label="AI ingest"
          hint="When this is on, AI cameras in this store are analysed. Live video is separate."
          checked={s.ai_ingest !== false}
          disabled={!canEdit || ingestPending}
          onChange={onIngest}
        />
        <StoreSwitch
          label="Live view over HLS"
          hint="When a browser can't play a camera's original stream over WebRTC, play it over HLS. HLS starts a full keyframe interval later and its video passes through the IntraScene server."
          checked={s.live_hls_fallback === true}
          disabled={!canEdit || hlsPending}
          onChange={onHls}
        />
        {s.is_simulation && (
          <StoreSwitch
            label="Live video"
            hint="When this is off, this store's clips are not published, and live view of those cameras stops."
            checked={s.sim_live !== false}
            disabled={!canEdit || livePending}
            onChange={onLive}
          />
        )}
        {s.is_simulation && s.sim_start_time && (
          <div className="flex items-center gap-2">
            <Clock size={13} className="shrink-0" />
            <span className="font-mono text-xs">offset {s.sim_start_time}</span>
          </div>
        )}
        {s.is_simulation && (s.sim_offsets ?? []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <Bookmark size={13} className="shrink-0 text-bf-muted" />
            {(s.sim_offsets ?? []).map((off) => {
              const active = off.offset === s.sim_start_time;
              return (
                <button
                  key={`${off.label}:${off.offset}`}
                  type="button"
                  disabled={!canEdit || jumpPending || replayPending}
                  onClick={() => canEdit && onJump(off.offset)}
                  className={
                    "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset " +
                    (active
                      ? "bg-bf-violet/20 text-bf-violet ring-bf-violet/40"
                      : "bg-white/5 text-bf-muted ring-white/10 hover:text-bf-text disabled:hover:text-bf-muted")
                  }
                >
                  {off.label}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-2">
          <ScanText size={13} className="shrink-0" />
          <span>
            Smallest box {(s.min_crop_area_px2 ?? 12000).toLocaleString()} px²
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Expand size={13} className="shrink-0" />
          <span>Crop context {s.crop_expand_pct ?? 20}%</span>
        </div>
      </dl>

      <div className="mt-auto flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="bf-button bg-white/10 bg-none"
            onClick={onView}
          >
            <Eye size={16} className="mr-2" /> View this store
          </button>
          {canEdit && s.is_simulation && (
            <button
              type="button"
              className="bf-button bg-white/10 bg-none"
              disabled={replayPending || jumpPending}
              onClick={onReplay}
            >
              Start now
            </button>
          )}
        </div>
        {canEdit && (
          <button type="button" className="bf-button" onClick={onEdit}>
            <Pencil size={16} className="mr-2" /> Edit
          </button>
        )}
      </div>
    </article>
  );
}

const OFFSET_STEP_S = 1;
const OFFSET_MAX_S = 99 * 3600 + 59 * 60 + 59;
// Simulated camera paths are camNN: clips x streams per clip stays two digits.
const MAX_SIM_STREAMS = 256;

function normalizeCopies(value: number | undefined): number {
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

function parseStartOffsetS(value: string): number | null {
  const text = value.trim();
  if (!text) return 0;
  if (/^\d+(?:\.\d+)?$/.test(text)) return Math.floor(Number(text));
  const parts = text.split(":");
  if (parts.length !== 2 && parts.length !== 3) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  const s = parts.length === 3 ? Number(parts[2]) : 0;
  if (![h, m, s].every((n) => Number.isFinite(n))) return null;
  if (m < 0 || m > 59 || s < 0 || s >= 60) return null;
  return Math.floor(h * 3600 + m * 60 + s);
}

function formatStartOffset(total: number): string {
  const clamped = Math.max(0, Math.min(OFFSET_MAX_S, Math.round(total)));
  if (clamped === 0) return "";
  const h = Math.floor(clamped / 3600);
  const m = Math.floor((clamped % 3600) / 60);
  const s = clamped % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function stepStartOffset(value: string, delta: number): string {
  const parsed = parseStartOffsetS(value);
  return formatStartOffset((parsed ?? 0) + delta);
}

function OffsetNudge({
  label,
  disabled,
  onStep,
  children,
}: {
  label: string;
  disabled?: boolean;
  onStep: () => void;
  children: ReactNode;
}) {
  const onStepRef = useRef(onStep);
  onStepRef.current = onStep;
  const holdRef = useRef<{ timeout: number; interval: number } | null>(null);

  function stop() {
    const hold = holdRef.current;
    if (!hold) return;
    window.clearTimeout(hold.timeout);
    window.clearInterval(hold.interval);
    holdRef.current = null;
  }

  function start() {
    if (disabled) return;
    onStepRef.current();
    const timeout = window.setTimeout(() => {
      const interval = window.setInterval(() => onStepRef.current(), 80);
      if (holdRef.current) holdRef.current.interval = interval;
    }, 400);
    holdRef.current = { timeout, interval: 0 };
  }

  useEffect(() => stop, []);

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      className="flex flex-1 items-center justify-center text-bf-muted hover:bg-white/5 hover:text-bf-text disabled:opacity-30"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);
        start();
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
    >
      {children}
    </button>
  );
}

function StoreDraftModal({
  draft,
  simFolders,
  libraryMounted,
  takenNames,
  pending,
  onClose,
  onSubmit,
  onDelete,
}: {
  draft: StoreInput & { existing?: string };
  simFolders: SimVideoFolder[];
  libraryMounted: boolean;
  takenNames: string[];
  pending: boolean;
  onClose: () => void;
  onSubmit: (body: StoreInput) => void;
  onDelete?: () => void;
}) {
  const [newOffsetLabel, setNewOffsetLabel] = useState("");
  const [newOffsetTime, setNewOffsetTime] = useState("");
  const [form, setForm] = useState<StoreInput>({
    name: draft.name,
    display_name: draft.display_name ?? "",
    location: draft.location ?? "",
    is_simulation: draft.is_simulation ?? false,
    sim_videos_subdir: draft.sim_videos_subdir ?? "",
    sim_start_time: draft.sim_start_time ?? "",
    sim_loop: draft.sim_loop ?? true,
    sim_live: draft.sim_live ?? true,
    sim_offsets: draft.sim_offsets ?? [],
    sim_copies: draft.sim_copies ?? 1,
    min_crop_area_px2: draft.min_crop_area_px2 ?? 12000,
    crop_expand_pct: draft.crop_expand_pct ?? 20,
    ai_ingest: draft.ai_ingest ?? true,
    customer_id: draft.customer_id ?? null,
  });
  const editing = Boolean(draft.existing);
  const customers = useQuery({ queryKey: ["customers"], queryFn: api.customers.list });

  const nameTaken = takenNames.some(
    (n) =>
      n.toLowerCase() === form.name.trim().toLowerCase() &&
      n !== draft.existing,
  );
  const needsFolder = Boolean(form.is_simulation) && !form.sim_videos_subdir;
  const chosenFolder = simFolders.find((f) => f.name === form.sim_videos_subdir);
  const folderEmpty = Boolean(chosenFolder && chosenFolder.clips === 0);
  const folderChanged =
    editing &&
    (form.sim_videos_subdir || "") !== (draft.sim_videos_subdir || "");
  const copies = normalizeCopies(form.sim_copies);
  const copiesChanged = editing && copies !== (draft.sim_copies ?? 1);
  const maxCopies = chosenFolder?.clips
    ? Math.max(1, Math.floor(MAX_SIM_STREAMS / chosenFolder.clips))
    : MAX_SIM_STREAMS;
  const simCameras = (chosenFolder?.clips ?? 0) * copies;

  return (
    <Modal onClose={onClose} labelledBy="store-draft-title" panelClassName="w-full max-w-lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            ...form,
            name: form.name.trim(),
            sim_videos_subdir: form.is_simulation ? form.sim_videos_subdir : "",
            sim_start_time: form.is_simulation ? form.sim_start_time : "",
            sim_loop: form.is_simulation ? Boolean(form.sim_loop) : true,
            sim_live: form.is_simulation ? Boolean(form.sim_live ?? true) : true,
            sim_offsets: form.is_simulation ? form.sim_offsets ?? [] : [],
            sim_copies: form.is_simulation ? copies : 1,
            min_crop_area_px2: Number.isFinite(Number(form.min_crop_area_px2))
              ? Math.max(0, Math.round(Number(form.min_crop_area_px2)))
              : 12000,
            crop_expand_pct: Number.isFinite(Number(form.crop_expand_pct))
              ? Math.max(0, Math.min(100, Math.round(Number(form.crop_expand_pct))))
              : 20,
            ai_ingest: Boolean(form.ai_ingest ?? true),
          });
        }}
      >
        <h3 id="store-draft-title" className="text-lg font-medium text-white">
          {editing ? `Edit ${draft.existing}` : "Add a store"}
        </h3>

        <div className="block">
          <FieldLabel
            htmlFor="store-name"
            hint="Used internally to tag this store's cameras and footage. The display name is what people see."
          >
            Store name
          </FieldLabel>
          <input
            id="store-name"
            className="bf-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus={!editing}
            placeholder="north-side"
          />
          {nameTaken && (
            <span className="mt-1 block text-xs text-amber-300">
              A store with that name already exists.
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="block">
            <FieldLabel htmlFor="store-display">Display name (optional)</FieldLabel>
            <input
              id="store-display"
              className="bf-input"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="North Side"
            />
          </div>
          <div className="block">
            <FieldLabel htmlFor="store-location">Location (optional)</FieldLabel>
            <input
              id="store-location"
              className="bf-input"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="1200 Main St"
            />
          </div>
        </div>

        <div className="block">
          <FieldLabel hint="The customer whose people see this store as one of their locations, with its cameras, recorders and connectors. Staff always see every store.">
            Customer
          </FieldLabel>
          <Select
            ariaLabel="Customer"
            value={form.customer_id == null ? "" : String(form.customer_id)}
            onChange={(v) => setForm({ ...form, customer_id: v ? Number(v) : null })}
            options={[
              { value: "", label: "None (staff only)" },
              ...(customers.data ?? []).map((c) => ({ value: String(c.id), label: c.name })),
            ]}
          />
        </div>

        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-bf-violet"
              checked={Boolean(form.is_simulation)}
              onChange={(e) =>
                setForm({ ...form, is_simulation: e.target.checked })
              }
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-bf-text">
                  Simulation store
                </span>
                <InfoHint>
                  Cameras stream from recorded clips instead of live hardware —
                  useful for demos and testing without a recorder. Each
                  simulation store plays its own folder of clips, and they all
                  run at once.
                </InfoHint>
              </span>
              <span className="block text-xs text-bf-muted">
                Streams come from recorded video files.
              </span>
            </span>
          </label>

          {form.is_simulation && (
            <div className="mt-3 space-y-2 border-t border-white/5 pt-3">
              <span className="mb-1 flex items-center gap-1.5">
                <span className="text-xs font-medium text-bf-muted">
                  Video folder
                </span>
                <InfoHint>
                  A folder in the simulation clip library on the server. Every
                  video file in it is published as cameras on this store
                  automatically — eight clips at two streams per clip make
                  sixteen cameras.
                </InfoHint>
              </span>
              {libraryMounted && simFolders.length > 0 ? (
                <SearchableSelect
                  ariaLabel="Simulation video folder"
                  value={form.sim_videos_subdir ?? ""}
                  onChange={(v) => setForm({ ...form, sim_videos_subdir: v })}
                  placeholder="Choose a folder…"
                  options={simFolders.map((f) => ({
                    value: f.name,
                    label: f.name,
                    hint: `${f.clips} clip${f.clips === 1 ? "" : "s"}`,
                    disabled: f.clips === 0,
                  }))}
                />
              ) : (
                <input
                  className="bf-input font-mono text-xs"
                  value={form.sim_videos_subdir}
                  onChange={(e) =>
                    setForm({ ...form, sim_videos_subdir: e.target.value })
                  }
                  placeholder="sim1"
                />
              )}
              {!libraryMounted && (
                <p className="text-xs text-bf-muted">
                  The clip library is not readable from here, so the folder name
                  cannot be checked.
                </p>
              )}
              {needsFolder && (
                <p className="text-xs text-amber-300">
                  Pick a folder — a simulation store with no clips has no
                  cameras to stream.
                </p>
              )}
              {folderEmpty && (
                <p className="text-xs text-amber-300">
                  That folder has no video files in it.
                </p>
              )}
              <div className="pt-1">
                <span className="mb-1 flex items-center gap-1.5">
                  <label
                    htmlFor="store-sim-copies"
                    className="text-xs font-medium text-bf-muted"
                  >
                    Streams per clip
                  </label>
                  <InfoHint>
                    Every stream is its own camera, starting at a different
                    point in the clip. A store has at most {MAX_SIM_STREAMS}{" "}
                    simulated cameras.
                  </InfoHint>
                </span>
                <input
                  id="store-sim-copies"
                  type="number"
                  min={1}
                  max={maxCopies}
                  step={1}
                  className="bf-input w-24 font-mono text-sm"
                  value={form.sim_copies ?? 1}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      sim_copies: e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                />
                <p className="mt-1 text-xs text-bf-muted">
                  Each clip is published this many times, evenly spaced in time.
                </p>
                {copies > maxCopies && (
                  <p className="mt-1 text-xs text-amber-300">
                    This folder allows at most {maxCopies} stream{maxCopies === 1 ? "" : "s"} per
                    clip.
                  </p>
                )}
              </div>
              {chosenFolder &&
                chosenFolder.clips > 0 &&
                (!editing || folderChanged || copiesChanged) && (
                  <p className="text-xs text-bf-muted">
                    {folderChanged
                      ? `${simCameras} camera${simCameras === 1 ? "" : "s"} will replace this store's simulated cameras.`
                      : editing
                        ? `This store will have ${simCameras} simulated camera${simCameras === 1 ? "" : "s"}.`
                        : `${simCameras} camera${simCameras === 1 ? "" : "s"} will be created from this folder.`}
                  </p>
                )}
              <div className="pt-1">
                <span className="mb-1 flex items-center gap-1.5">
                  <span className="text-xs font-medium text-bf-muted">
                    Start offset
                  </span>
                  <InfoHint>
                    How far from the beginning of each clip to start — not a
                    clock. 1:30:00 skips the first hour and a half. Saving
                    restarts the simulator and reconnects those cameras
                    immediately.
                  </InfoHint>
                </span>
                <div className="flex items-center gap-2">
                  <div className="flex min-w-0 flex-1 items-stretch">
                    <input
                      id="store-sim-start"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="1:30:00"
                      className="bf-input rounded-r-none font-mono text-sm"
                      value={form.sim_start_time ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, sim_start_time: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                        e.preventDefault();
                        const delta = e.key === "ArrowUp" ? OFFSET_STEP_S : -OFFSET_STEP_S;
                        setForm((f) => ({
                          ...f,
                          sim_start_time: stepStartOffset(f.sim_start_time ?? "", delta),
                        }));
                      }}
                      aria-label="Start offset from the beginning of each clip"
                    />
                    <div className="flex w-8 flex-col overflow-hidden rounded-r-lg border border-l-0 border-white/10 bg-bf-surface">
                      <OffsetNudge
                        label="Increase start offset by one second"
                        onStep={() =>
                          setForm((f) => ({
                            ...f,
                            sim_start_time: stepStartOffset(
                              f.sim_start_time ?? "",
                              OFFSET_STEP_S,
                            ),
                          }))
                        }
                      >
                        <ChevronUp size={14} />
                      </OffsetNudge>
                      <OffsetNudge
                        label="Decrease start offset by one second"
                        disabled={!parseStartOffsetS(form.sim_start_time ?? "")}
                        onStep={() =>
                          setForm((f) => ({
                            ...f,
                            sim_start_time: stepStartOffset(
                              f.sim_start_time ?? "",
                              -OFFSET_STEP_S,
                            ),
                          }))
                        }
                      >
                        <ChevronDown size={14} />
                      </OffsetNudge>
                    </div>
                  </div>
                  {form.sim_start_time && (
                    <button
                      type="button"
                      className="text-xs text-bf-muted hover:text-bf-text"
                      onClick={() => setForm({ ...form, sim_start_time: "" })}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-bf-muted">
                  Hours:minutes:seconds from the start of the video.
                </p>
              </div>
              <label className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-bf-violet"
                  checked={Boolean(form.sim_loop)}
                  onChange={(e) =>
                    setForm({ ...form, sim_loop: e.target.checked })
                  }
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-bf-muted">
                      Loop clips
                    </span>
                    <InfoHint>
                      When a clip reaches the end, it starts over from the
                      beginning. Off: cameras stop there until you play the
                      simulation again.
                    </InfoHint>
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-bf-violet"
                  checked={Boolean(form.sim_live ?? true)}
                  onChange={(e) =>
                    setForm({ ...form, sim_live: e.target.checked })
                  }
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-bf-muted">
                      Live video
                    </span>
                    <InfoHint>
                      When this is off, this store's clips are not published,
                      and live view of those cameras stops.
                    </InfoHint>
                  </span>
                </span>
              </label>
              <div className="pt-1">
                <span className="mb-1 flex items-center gap-1.5">
                  <span className="text-xs font-medium text-bf-muted">
                    Saved positions
                  </span>
                  <InfoHint>
                    Named points in the clip. On the store card they jump the
                    simulation to that offset immediately.
                  </InfoHint>
                </span>
                {(form.sim_offsets ?? []).length > 0 && (
                  <ul className="mb-2 space-y-1.5">
                    {(form.sim_offsets ?? []).map((off, idx) => (
                      <li
                        key={`${off.label}:${off.offset}:${idx}`}
                        className="flex items-center gap-2"
                      >
                        <button
                          type="button"
                          className={
                            "min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-xs ring-1 ring-inset " +
                            (off.offset === form.sim_start_time
                              ? "bg-bf-violet/20 text-bf-violet ring-bf-violet/40"
                              : "bg-white/5 text-bf-text ring-white/10 hover:bg-white/10")
                          }
                          onClick={() =>
                            setForm({ ...form, sim_start_time: off.offset })
                          }
                        >
                          <span className="font-medium">{off.label}</span>
                          <span className="ml-2 font-mono text-bf-muted">
                            {off.offset}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="text-xs text-bf-muted hover:text-red-400"
                          onClick={() =>
                            setForm({
                              ...form,
                              sim_offsets: (form.sim_offsets ?? []).filter(
                                (_, i) => i !== idx,
                              ),
                            })
                          }
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    className="bf-input min-w-[8rem] flex-1 text-sm"
                    placeholder="Opening"
                    value={newOffsetLabel}
                    onChange={(e) => setNewOffsetLabel(e.target.value)}
                    aria-label="Name for a saved position"
                  />
                  <input
                    className="bf-input w-28 font-mono text-sm"
                    placeholder="1:30:00"
                    value={newOffsetTime}
                    onChange={(e) => setNewOffsetTime(e.target.value)}
                    aria-label="Offset for a saved position"
                  />
                  <button
                    type="button"
                    className="text-xs text-bf-violet hover:text-bf-text disabled:opacity-40"
                    disabled={
                      !newOffsetLabel.trim() ||
                      !newOffsetTime.trim() ||
                      parseStartOffsetS(newOffsetTime) === null
                    }
                    onClick={() => {
                      const next: SimOffset = {
                        label: newOffsetLabel.trim(),
                        offset: newOffsetTime.trim(),
                      };
                      setForm({
                        ...form,
                        sim_offsets: [...(form.sim_offsets ?? []), next],
                      });
                      setNewOffsetLabel("");
                      setNewOffsetTime("");
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 accent-bf-violet"
            checked={Boolean(form.ai_ingest ?? true)}
            onChange={(e) =>
              setForm({ ...form, ai_ingest: e.target.checked })
            }
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-bf-muted">
                AI ingest
              </span>
              <InfoHint>
                When this is on, AI cameras in this store are analysed. Live
                video is separate.
              </InfoHint>
            </span>
          </span>
        </label>

        <div className="block">
          <FieldLabel
            htmlFor="store-min-box"
            hint="People whose detection box is smaller than this are not described. 12,000 is typical for nearby people. Lower it when cameras see people farther away."
          >
            Smallest detection
          </FieldLabel>
          <div className="flex items-center gap-2">
            <input
              id="store-min-box"
              type="number"
              inputMode="numeric"
              min={0}
              max={8294400}
              step={100}
              className="bf-input font-mono"
              value={form.min_crop_area_px2 ?? 12000}
              onChange={(e) =>
                setForm({
                  ...form,
                  min_crop_area_px2: Number(e.target.value),
                })
              }
            />
            <span className="shrink-0 text-xs text-bf-muted">px²</span>
          </div>
          <p className="mt-1 text-xs text-bf-muted">
            Area of the detection box, in pixels.
          </p>
        </div>

        <div className="block">
          <FieldLabel
            htmlFor="store-crop-context"
            hint="How much surrounding frame is included with each person sent for description. 20 grows a 100×100 box to 120×120. A side against the image edge is clipped to the frame."
          >
            Crop context
          </FieldLabel>
          <div className="flex items-center gap-2">
            <input
              id="store-crop-context"
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              step={1}
              className="bf-input font-mono"
              value={form.crop_expand_pct ?? 20}
              onChange={(e) =>
                setForm({
                  ...form,
                  crop_expand_pct: Number(e.target.value),
                })
              }
            />
            <span className="shrink-0 text-xs text-bf-muted">%</span>
          </div>
          <p className="mt-1 text-xs text-bf-muted">
            Percent larger than the detection box.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          {editing && onDelete ? (
            <button
              type="button"
              className="bf-button bg-red-600 bg-none hover:bg-red-500"
              onClick={onDelete}
            >
              <Trash2 size={16} className="mr-2" /> Delete store
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" className="bf-button bg-white/10 bg-none" onClick={onClose}>
              Cancel
            </button>
            <button
              className="bf-button disabled:opacity-40"
              type="submit"
              disabled={pending || nameTaken || needsFolder || folderEmpty}
            >
              {pending ? "Saving…" : editing ? "Save changes" : "Create store"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
